<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import {
    fetchDashboard,
    fetchDeviceConfig,
    patchDeviceConfig,
    sendSpeakerTest,
    requestCameraPreview,
    fetchCameraPreview,
    fetchAdminLivekitToken,
    fetchAdminRooms,
    getDeviceDiagnostics,
    ApiError,
  } from "../lib/api";
  import { AdminSubscriberClient } from "../lib/livekit";
  import { push } from "svelte-spa-router";
  import type {
    DeviceConfig,
    AgentEnumeration,
    EnumCamera,
    CameraEntry,
    MediaCaps,
  } from "@diy-avatar/shared/admin";
  import type { TrackStats } from "../lib/livekit";

  export let label: string | undefined = undefined;

  const PREVIEW_TTL_S = 10;

  /** One editable camera row, merged from enumeration + cfg.cameras. */
  interface CameraRow {
    path: string;
    name: string;
    enabled: boolean;
    enum: EnumCamera | null;
  }

  let cfg: DeviceConfig | null = null;
  let enumeration: AgentEnumeration | null = null;
  let info: string | null = null;
  let error: string | null = null;
  let saving = false;
  let testing = false;
  let speakerTestInfo: string | null = null;

  let cameraRows: CameraRow[] = [];

  // ----- Expert media profile settings -----
  // UI selects use the sentinel string "auto"; it maps to null/undefined in the
  // persisted DeviceConfig on save (never the literal string "auto").
  const ALL_CODECS: Array<"h264" | "vp8" | "vp9"> = ["h264", "vp8", "vp9"];
  let mediaCaps: MediaCaps | null = null;
  let mediaPerf = "auto";
  let mediaCodec = "auto";
  let mediaRoles: Record<string, string> = {};

  // LiveKit subscriber (live tiles for enabled cameras).
  let subscriber: AdminSubscriberClient | null = null;
  let lkNote: string | null = null;
  let statsTimer: ReturnType<typeof setInterval> | null = null;
  let liveStats: Record<string, TrackStats> = {};
  let videoEls: Record<string, HTMLVideoElement> = {};

  // Still-preview polling state (disabled cameras), keyed by path.
  interface PreviewState {
    img: string | null;
    countdown: number;
    timer: ReturnType<typeof setInterval> | null;
    note: string | null;
  }
  let previews: Record<string, PreviewState> = {};

  function buildRows() {
    if (!cfg) return;
    const cams = enumeration
      ? enumeration.cameras.filter((c) => c.capture !== false)
      : [];
    const byPath = new Map<string, CameraEntry>();
    for (const e of cfg.cameras ?? []) byPath.set(e.path, e);
    const rows: CameraRow[] = [];
    for (const c of cams) {
      const existing = byPath.get(c.path);
      rows.push({
        path: c.path,
        name: existing?.name ?? c.label ?? c.name,
        enabled: existing?.enabled ?? false,
        enum: c,
      });
      byPath.delete(c.path);
    }
    // Configured cameras no longer present in enumeration: keep them editable.
    for (const e of byPath.values()) {
      rows.push({ path: e.path, name: e.name, enabled: e.enabled, enum: null });
    }
    cameraRows = rows;
  }

  /**
   * Which video codecs the device actually probed, derived from the media-caps
   * diagnostics (benchmark results + VA-API decode profiles). Empty when there
   * is no probe data — the expert codec dropdown then falls back to all codecs.
   */
  function deriveProbedCodecs(caps: MediaCaps | null): Set<string> {
    const set = new Set<string>();
    if (!caps) return set;
    for (const r of caps.bench?.results ?? []) {
      const c = (r.codec ?? "").toLowerCase();
      if (c === "h264" || c === "vp8" || c === "vp9") set.add(c);
    }
    for (const p of caps.va_profiles ?? []) {
      const s = (p.profile ?? "").toLowerCase();
      if (s.includes("h264")) set.add("h264");
      if (s.includes("vp9")) set.add("vp9");
      else if (s.includes("vp8")) set.add("vp8");
    }
    return set;
  }

  /** Seed the expert-media UI selects from the loaded config + camera rows. */
  function initMediaState() {
    if (!cfg) return;
    mediaPerf = cfg.mediaPerformanceLevel ?? "auto";
    mediaCodec = cfg.mediaCodecOverride ?? "auto";
    const roles: Record<string, string> = {};
    for (const row of cameraRows) {
      roles[row.path] = cfg.mediaCameraRoles?.[row.path] ?? "auto";
    }
    mediaRoles = roles;
  }

  /**
   * Best-effort per-device media diagnostics fetch used only to narrow the
   * expert dropdowns to probed options. Needs a device label; the single-device
   * (labelless) path has no diagnostics endpoint, so we degrade to "show all".
   */
  async function loadDiagnostics() {
    if (!label) return;
    try {
      const res = await getDeviceDiagnostics(label);
      mediaCaps = res.device.media_caps;
    } catch {
      mediaCaps = null;
    }
  }

  /** Fold the expert-media selects back into cfg ("auto" ⇒ null/undefined). */
  function syncMediaIntoCfg() {
    if (!cfg) return;
    cfg.mediaPerformanceLevel =
      mediaPerf === "auto"
        ? undefined
        : (mediaPerf as NonNullable<DeviceConfig["mediaPerformanceLevel"]>);
    cfg.mediaCodecOverride =
      mediaCodec === "auto" ? null : (mediaCodec as "h264" | "vp8" | "vp9");
    const roles: Record<string, "board" | "face"> = {};
    for (const [path, role] of Object.entries(mediaRoles)) {
      if (role === "board" || role === "face") roles[path] = role;
    }
    cfg.mediaCameraRoles = roles;
  }

  /**
   * Kiosk-identical card label: `card N: <id> [out]/[in]`. Sourced from the
   * SAME `enumeration.audio_cards` the kiosk uses so both screens show the same
   * physical ALSA cards (not the PortAudio/ALSA-virtual PCM list).
   */
  function cardLabel(c: import("@diy-avatar/shared/admin").EnumAudioCard): string {
    const caps: string[] = [];
    if (c.playback) caps.push("out");
    if (c.capture) caps.push("in");
    const tag = caps.length ? ` [${caps.join("/")}]` : "";
    return `card ${c.index}: ${c.id || c.name || "?"}${tag}`;
  }

  /** Selected card value: prefer the stable id, fall back to the index string. */
  function cardValue(c: import("@diy-avatar/shared/admin").EnumAudioCard): string {
    return c.id || String(c.index);
  }

  function formatDetails(c: EnumCamera | null): string {
    if (!c || !c.formats || c.formats.length === 0) return "no format info";
    return c.formats
      .map((f) => {
        const sizes = f.sizes
          .map((s) => {
            const fps = s.fps && s.fps.length > 0 ? `@${s.fps.join("/")}` : "";
            return `${s.width}x${s.height}${fps}`;
          })
          .join(", ");
        return `${f.fourcc}: ${sizes}`;
      })
      .join(" · ");
  }

  $: enabledCount = cameraRows.filter((r) => r.enabled).length;

  // Expert codec dropdown: only probed codecs when we have probe data, else all.
  // The currently-saved override is always kept selectable so it never vanishes.
  $: hasProbe = mediaCaps !== null;
  $: probedCodecs = deriveProbedCodecs(mediaCaps);
  $: codecOptions = (() => {
    const base =
      hasProbe && probedCodecs.size > 0 ? [...probedCodecs] : [...ALL_CODECS];
    const s = new Set<string>(base);
    if (mediaCodec !== "auto") s.add(mediaCodec);
    return ALL_CODECS.filter((c) => s.has(c));
  })();

  async function load() {
    // Load config and enumeration INDEPENDENTLY: the config form must render
    // even when enumeration (used only to populate the device dropdowns) is
    // unavailable, and a missing enumeration must never blank the saved config.
    try {
      const dc = await fetchDeviceConfig(label);
      // Defensive default: backend always sends masterVolume, but tolerate an
      // older payload that predates the field (OS master volume, [0,100]).
      cfg = {
        ...dc.config,
        masterVolume: dc.config.masterVolume ?? 100,
        audioCard: dc.config.audioCard ?? "",
        audioCardIn: dc.config.audioCardIn ?? "",
        externalSourceBackend: dc.config.externalSourceBackend ?? "disabled",
        externalSourceBoardIp: dc.config.externalSourceBoardIp ?? "",
        externalSourceBoardName: dc.config.externalSourceBoardName ?? "",
        externalSourceClientUrl: dc.config.externalSourceClientUrl ?? "",
      };
      error = null;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "load failed";
    }
    try {
      const dash = await fetchDashboard();
      enumeration = dash.device.enumeration;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      // Enumeration is best-effort — leave dropdowns at their current value.
      enumeration = null;
    }
    // Build the camera rows from the (possibly null) enumeration + saved config,
    // then bring up the LiveKit subscriber for live tiles. Both are best-effort
    // and must run regardless of whether enumeration loaded.
    buildRows();
    initMediaState();
    void connectLivekit();
    void loadDiagnostics();
  }

  /**
   * Find the room this device lives in (room.devices includes label) and
   * connect a subscriber so enabled cameras get live tiles. Defensive: any
   * failure is surfaced as an inline note and never breaks the config form.
   */
  async function connectLivekit() {
    try {
      if (!label) {
        lkNote = "No device selected — live preview unavailable.";
        return;
      }
      const rooms = await fetchAdminRooms();
      const room = rooms.rooms.find((r) => r.devices.includes(label));
      if (!room) {
        lkNote = "No room bound to this device — live preview unavailable.";
        return;
      }
      const tok = await fetchAdminLivekitToken(room.name);
      subscriber = new AdminSubscriberClient(
        { iceServers: tok.iceServers },
        () => attachLiveTiles(),
      );
      await subscriber.connect(tok.url, tok.token);
      attachLiveTiles();
      statsTimer = setInterval(() => void pollStats(), 1500);
    } catch (err) {
      lkNote =
        err instanceof Error
          ? `Live preview unavailable: ${err.message}`
          : "Live preview unavailable.";
    }
  }

  function attachLiveTiles() {
    if (!subscriber) return;
    for (const row of cameraRows) {
      if (!row.enabled) continue;
      const el = videoEls[row.path];
      if (el) subscriber.attachTrack(row.name, el);
    }
  }

  async function pollStats() {
    if (!subscriber) return;
    await subscriber.pollStats();
    const next: Record<string, TrackStats> = {};
    for (const row of cameraRows) {
      if (!row.enabled) continue;
      const s = subscriber.getStats(row.name);
      if (s) next[row.path] = s;
    }
    liveStats = next;
  }

  function statsLabel(s: TrackStats | undefined): string {
    if (!s) return "…";
    const res = s.width && s.height ? `${s.width}x${s.height}` : "?";
    const fps = s.fps != null ? `${Math.round(s.fps)}fps` : "?fps";
    const br = s.kbps != null ? `${s.kbps}kbps` : "?kbps";
    return `${res} · ${fps} · ${br}`;
  }

  function stopPreview(path: string) {
    const p = previews[path];
    if (p?.timer) clearInterval(p.timer);
    if (p) p.timer = null;
  }

  async function startPreview(path: string) {
    stopPreview(path);
    previews[path] = {
      img: null,
      countdown: PREVIEW_TTL_S,
      timer: null,
      note: "starting…",
    };
    previews = { ...previews };
    try {
      await requestCameraPreview(path, PREVIEW_TTL_S, label);
    } catch (err) {
      previews[path].note =
        err instanceof Error ? err.message : "preview request failed";
      previews = { ...previews };
      return;
    }
    previews[path].note = null;
    previews = { ...previews };
    const timer = setInterval(async () => {
      const st = previews[path];
      if (!st) return;
      st.countdown -= 1;
      if (st.countdown <= 0) {
        stopPreview(path);
        st.note = "expired";
        previews = { ...previews };
        return;
      }
      try {
        const frame = await fetchCameraPreview(path, label);
        if (frame && frame.error) {
          // The device reported a capture failure (e.g. the camera is busy
          // because the kiosk holds it open). Surface it and stop polling.
          stopPreview(path);
          st.img = null;
          st.note = frame.error;
        } else if (frame && frame.jpeg_b64) {
          st.img = `data:image/jpeg;base64,${frame.jpeg_b64}`;
          st.note = null;
        } else {
          st.note = "waiting for frame…";
        }
      } catch (err) {
        st.note = err instanceof Error ? err.message : "preview failed";
      }
      previews = { ...previews };
    }, 1000);
    previews[path].timer = timer;
    previews = { ...previews };
  }

  function syncCamerasIntoCfg() {
    if (!cfg) return;
    // Persist only operator-configured cameras: enabled ones, plus any that
    // were already in cfg.cameras (so an explicit disable survives a save).
    const prev = new Set((cfg.cameras ?? []).map((c) => c.path));
    cfg.cameras = cameraRows
      .filter((r) => r.enabled || prev.has(r.path))
      .map((r) => ({ path: r.path, name: r.name.trim(), enabled: r.enabled }));
  }

  async function save() {
    if (!cfg) return;
    syncCamerasIntoCfg();
    syncMediaIntoCfg();
    saving = true;
    info = null;
    error = null;
    try {
      const res = await patchDeviceConfig(cfg, label);
      cfg = res.config;
      buildRows();
      initMediaState();
      attachLiveTiles();
      info = "saved";
    } catch (err) {
      error =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "save failed";
    } finally {
      saving = false;
    }
  }

  async function testSpeaker() {
    if (!cfg) return;
    testing = true;
    speakerTestInfo = null;
    error = null;
    try {
      // Card-based selection: we no longer pin a named PCM sink, so the speaker
      // test plays on the device's DEFAULT output — which is the selected output
      // card once it is applied (the agent pins it as the ALSA default via
      // /etc/asound.conf). `speaker` stays "auto" so the agent/browser use that
      // default rather than a (now-unused) named PCM.
      const res = await sendSpeakerTest({ speaker: cfg.speaker }, label);
      speakerTestInfo = res.speaker_label
        ? `Playing tone on "${res.speaker_label}"…`
        : "Playing tone on the device's selected output card…";
    } catch (err) {
      error =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "speaker test failed";
    } finally {
      testing = false;
    }
  }

  onMount(load);

  onDestroy(() => {
    if (statsTimer) clearInterval(statsTimer);
    for (const path of Object.keys(previews)) stopPreview(path);
    void subscriber?.disconnect();
  });
</script>

<section>
  <h1>Device config</h1>
  {#if error}
    <p class="error" data-testid="error">{error}</p>
  {/if}
  {#if cfg}
    <form on:submit|preventDefault={save} data-testid="device-config-form">
      <fieldset class="cameras" data-testid="cameras">
        <legend>Cameras</legend>
        {#if lkNote}
          <small class="hint" data-testid="lk-note">{lkNote}</small>
        {/if}
        {#if enabledCount === 0}
          <small class="hint" data-testid="no-camera-hint">
            No camera enabled — the kiosk will publish no classroom video.
          </small>
        {/if}
        {#if cameraRows.length === 0}
          <small class="muted">No capture-capable cameras enumerated.</small>
        {/if}
        {#each cameraRows as row (row.path)}
          <div class="camera-row" data-testid="camera-row">
            <div class="camera-head">
              <input
                type="text"
                class="camera-name"
                bind:value={row.name}
                placeholder="Camera name"
                data-testid="camera-name"
                aria-label="Camera name for {row.path}"
              />
              <label class="toggle">
                <input
                  type="checkbox"
                  bind:checked={row.enabled}
                  data-testid="camera-enabled"
                />
                Enabled
              </label>
            </div>
            <small class="muted">
              {row.path}{#if row.enum} — {row.enum.label ?? row.enum.name}{/if}
            </small>
            <small class="details" data-testid="camera-details">
              {formatDetails(row.enum)}
            </small>
            {#if row.enabled}
              <div class="preview live">
                <!-- svelte-ignore a11y-media-has-caption -->
                <video
                  bind:this={videoEls[row.path]}
                  autoplay
                  playsinline
                  muted
                  data-testid="camera-live"
                ></video>
                <span class="stats" data-testid="camera-stats">
                  {statsLabel(liveStats[row.path])}
                </span>
              </div>
            {:else}
              <div class="preview still">
                <button
                  type="button"
                  class="test"
                  on:click={() => startPreview(row.path)}
                  data-testid="camera-preview-btn"
                >
                  Preview
                </button>
                {#if previews[row.path]}
                  {#if previews[row.path].img}
                    <img
                      src={previews[row.path].img}
                      alt="Camera preview"
                      data-testid="camera-preview-img"
                    />
                  {/if}
                  {#if previews[row.path].timer}
                    <span class="stats" data-testid="camera-preview-countdown">
                      {previews[row.path].countdown}s
                    </span>
                  {/if}
                  {#if previews[row.path].note}
                    <small class="hint">{previews[row.path].note}</small>
                  {/if}
                {/if}
              </div>
            {/if}
          </div>
        {/each}
      </fieldset>
      <label>
        Speaker (output card)
        <div class="row">
          <select bind:value={cfg.audioCard} data-testid="audio-card">
            <option value="">auto (device picks)</option>
            {#if enumeration && enumeration.audio_cards}
              {#each enumeration.audio_cards.filter((c) => c.playback) as c}
                <option value={cardValue(c)}>{cardLabel(c)}</option>
              {/each}
            {/if}
          </select>
          <button
            type="button"
            class="test"
            on:click={testSpeaker}
            disabled={testing}
            data-testid="speaker-test"
            title="Play a test tone on the device's selected output card"
          >
            {testing ? "Testing…" : "Test"}
          </button>
        </div>
        {#if speakerTestInfo}
          <small class="hint" data-testid="speaker-test-info">{speakerTestInfo}</small>
        {/if}
        <small class="hint">
          The physical ALSA playback card the device pins as its default output
          (writes /etc/asound.conf). Same list the device's own Settings → Audio
          screen shows. Applied live; a kiosk restart may be needed for Chromium.
        </small>
      </label>
      <label>
        Microphone (input card)
        <select bind:value={cfg.audioCardIn} data-testid="audio-card-in">
          <option value="">auto (follows output)</option>
          {#if enumeration && enumeration.audio_cards}
            {#each enumeration.audio_cards.filter((c) => c.capture) as c}
              <option value={cardValue(c)}>{cardLabel(c)}</option>
            {/each}
          {/if}
        </select>
        <small class="hint">
          The physical ALSA capture card. When different from the output card the
          device writes an asym default so a separate USB mic and speaker can both
          be the default at once.
        </small>
      </label>
      <label>
        Speaker volume
        <div class="volume-row">
          <input
            type="range"
            min="0"
            max="100"
            step="10"
            bind:value={cfg.masterVolume}
            data-testid="master-volume"
            aria-label="OS hardware speaker volume"
          />
          <span class="volume-readout" data-testid="master-volume-readout">
            {Math.round(cfg.masterVolume)}%
          </span>
        </div>
        <small class="hint">OS hardware/classroom speaker volume applied on the device.</small>
      </label>
      <label>
        Remote voice gain
        <div class="volume-row">
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            bind:value={cfg.volume}
            data-testid="volume"
            aria-label="Remote voice gain"
          />
          <span class="volume-readout" data-testid="volume-readout">
            {Math.round(cfg.volume * 100)}%
          </span>
        </div>
        <small class="hint">Scales only the remote person's voice (LiveKit stream gain).</small>
      </label>
      <label>
        Monitor
        <select bind:value={cfg.monitor} data-testid="monitor">
          <option value="auto">auto</option>
          <option value="none">none</option>
          {#if enumeration}
            {#each enumeration.displays as d}
              <option value={d.name}>{d.name} [{d.status}]</option>
            {/each}
          {/if}
        </select>
      </label>
      <label>
        LED backend
        <select bind:value={cfg.led_backend} data-testid="led_backend">
          <option value="esp32_serial">esp32_serial</option>
          <option value="blinkstick">blinkstick</option>
          <option value="none">none</option>
        </select>
      </label>
      <label>
        LED board
        <select bind:value={cfg.led_board} data-testid="led_board">
          <option value="esp8266">ESP8266</option>
          <option value="esp32">ESP32</option>
        </select>
      </label>
      <label>
        LED port
        <select bind:value={cfg.led_port} data-testid="led_port">
          <option value={cfg.led_port}>{cfg.led_port}</option>
          {#if enumeration}
            {#each enumeration.serial as s}
              {#if s.path !== cfg.led_port}
                <option value={s.path}>{s.path}</option>
              {/if}
            {/each}
          {/if}
        </select>
      </label>
      <fieldset class="external-source" data-testid="external-source">
        <legend>External source (digital board)</legend>
        <small class="hint">
          Receive a digital-board screen mirror and expose it as an extra
          camera. Once connected it appears under Cameras as
          “DigitalBoard” — enable + rename it there to show it to students.
        </small>
        <label>
          Backend
          <select
            bind:value={cfg.externalSourceBackend}
            data-testid="external_source_backend"
          >
            <option value="disabled">disabled</option>
            <option value="official_deb">EShare client (official)</option>
            <option value="opensource_headless"
              >EShare headless (open source)</option
            >
          </select>
        </label>
        {#if cfg.externalSourceBackend !== "disabled"}
          <label>
            Board IP (leave empty to auto-discover by name)
            <input
              type="text"
              bind:value={cfg.externalSourceBoardIp}
              placeholder="e.g. 192.168.1.50"
              data-testid="external_source_board_ip"
            />
          </label>
          <label>
            Board name
            <input
              type="text"
              bind:value={cfg.externalSourceBoardName}
              placeholder="e.g. Tafel-A101"
              data-testid="external_source_board_name"
            />
          </label>
          {#if cfg.externalSourceBackend === "official_deb"}
            <label>
              Client download URL
              <input
                type="text"
                bind:value={cfg.externalSourceClientUrl}
                placeholder="https://…/ubuntu.zip"
                data-testid="external_source_client_url"
              />
            </label>
          {/if}
        {/if}
      </fieldset>
      <fieldset class="media-profile" data-testid="media-profile">
        <legend>Media profile</legend>
        <small class="hint">
          How the kiosk publishes classroom video. Leave everything on “auto” to
          let the device self-tune from its hardware probe.
        </small>
        <details class="expert" data-testid="media-expert-details">
          <summary data-testid="media-expert-summary">Expert settings</summary>
          <div class="card danger" data-testid="media-expert">
            {#if !hasProbe}
              <small class="hint" data-testid="media-no-probe-hint">
                No hardware probe data — run a hardware check first (Devices →
                Diagnostics → Recheck) to narrow these to what this device
                supports. All options are shown for now.
              </small>
            {/if}
            <label>
              Performance level
              <select
                bind:value={mediaPerf}
                data-testid="media-performance-level"
              >
                <option value="auto">auto</option>
                <option value="silent">silent</option>
                <option value="quiet">quiet</option>
                <option value="normal">normal</option>
                <option value="max">max</option>
              </select>
              <small class="hint">
                CPU / fan-noise vs. quality. auto ⇒ the backend picks “quiet”.
              </small>
            </label>
            <label>
              Codec override
              <select bind:value={mediaCodec} data-testid="media-codec-override">
                <option value="auto">auto</option>
                {#each codecOptions as c (c)}
                  <option value={c}>{c}</option>
                {/each}
              </select>
              <small class="hint">
                Force a published video codec. auto ⇒ LiveKit negotiates.
              </small>
            </label>
            <div class="camera-roles" data-testid="media-camera-roles">
              <span class="roles-title">Per-camera role</span>
              {#if cameraRows.length === 0}
                <small class="muted">No cameras to assign roles to.</small>
              {:else}
                {#each cameraRows as row (row.path)}
                  <label class="role-row">
                    <span class="role-name">{row.name || row.path}</span>
                    <select
                      bind:value={mediaRoles[row.path]}
                      data-testid={`media-camera-role-${row.path}`}
                    >
                      <option value="auto">auto</option>
                      <option value="board">board</option>
                      <option value="face">face</option>
                    </select>
                  </label>
                {/each}
              {/if}
              <small class="hint">
                board ⇒ favour sharpness; face ⇒ favour framerate.
              </small>
            </div>
          </div>
        </details>
      </fieldset>
      <button type="submit" disabled={saving} data-testid="save">
        {saving ? "Saving…" : "Save"}
      </button>
      {#if info}
        <p class="info" data-testid="info">{info}</p>
      {/if}
    </form>
  {:else}
    <p>Loading…</p>
  {/if}
</section>

<style>
  h1 {
    margin: 0 0 1rem;
  }
  form {
    display: grid;
    gap: 0.6rem;
    max-width: 480px;
    background: #111827;
    padding: 1rem;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  label {
    display: grid;
    gap: 0.25rem;
    color: #d1d5db;
    font-size: 0.85rem;
  }
  select {
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
    padding: 0.4rem;
  }
  .external-source {
    display: grid;
    gap: 0.5rem;
    border: 1px solid #1f2937;
    border-radius: 6px;
    padding: 0.6rem 0.8rem;
  }
  .external-source legend {
    color: #d1d5db;
    font-size: 0.85rem;
    padding: 0 0.3rem;
  }
  .external-source input[type="text"] {
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
    padding: 0.4rem;
  }
  .media-profile {
    display: grid;
    gap: 0.5rem;
    border: 1px solid #1f2937;
    border-radius: 6px;
    padding: 0.6rem 0.8rem;
  }
  .media-profile legend {
    color: #d1d5db;
    font-size: 0.85rem;
    padding: 0 0.3rem;
  }
  details.expert > summary {
    cursor: pointer;
    color: #93c5fd;
    font-size: 0.82rem;
    font-weight: 600;
    padding: 0.2rem 0;
    user-select: none;
  }
  .card {
    background: #0f172a;
    border: 1px solid #1f2937;
    border-radius: 6px;
    padding: 0.7rem 0.8rem;
    display: grid;
    gap: 0.6rem;
    margin-top: 0.5rem;
  }
  .card.danger {
    border-color: #7f1d1d;
  }
  .camera-roles {
    display: grid;
    gap: 0.4rem;
  }
  .roles-title {
    color: #d1d5db;
    font-size: 0.8rem;
    font-weight: 600;
  }
  .role-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }
  .role-name {
    color: #d1d5db;
    font-size: 0.8rem;
    word-break: break-word;
  }
  .role-row select {
    flex: 0 0 auto;
    min-width: 6rem;
  }
  button {
    padding: 0.5rem 0.8rem;
    background: #10b981;
    color: #04221a;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    justify-self: start;
  }
  .row {
    display: flex;
    gap: 0.4rem;
    align-items: stretch;
  }
  .row select {
    flex: 1;
  }
  .test {
    padding: 0.4rem 0.7rem;
    background: #2563eb;
    color: #eff6ff;
    border: none;
    border-radius: 4px;
    font-weight: 600;
    cursor: pointer;
    justify-self: unset;
  }
  .test:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .info {
    color: #6ee7b7;
  }
  .error {
    color: #fca5a5;
  }
  .hint {
    color: #fbbf24;
    font-size: 0.75rem;
  }
  .volume-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .volume-row input[type="range"] {
    flex: 1;
    accent-color: #10b981;
  }
  .volume-readout {
    min-width: 3.5rem;
    text-align: right;
    color: #f3f4f6;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }
  .cameras {
    border: 1px solid #1f2937;
    border-radius: 6px;
    padding: 0.6rem;
    display: grid;
    gap: 0.6rem;
  }
  .cameras legend {
    color: #d1d5db;
    font-size: 0.85rem;
    padding: 0 0.3rem;
  }
  .camera-row {
    display: grid;
    gap: 0.3rem;
    padding: 0.5rem;
    background: #0f172a;
    border: 1px solid #1f2937;
    border-radius: 6px;
  }
  .camera-head {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  .camera-name {
    flex: 1;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
    padding: 0.4rem;
  }
  .toggle {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    color: #d1d5db;
    font-size: 0.8rem;
  }
  .muted {
    color: #6b7280;
    font-size: 0.72rem;
  }
  .details {
    color: #93c5fd;
    font-size: 0.72rem;
    word-break: break-word;
  }
  .preview {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
  }
  .preview video,
  .preview img {
    max-width: 100%;
    width: 320px;
    border-radius: 4px;
    background: #000;
  }
  .stats {
    color: #f3f4f6;
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
    background: rgba(0, 0, 0, 0.6);
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
  }
</style>
